import { AccountApi } from '@/api/account.api';
import { VideoApi } from '@/api/video.api';
import { SupportedEnvironments } from '@/config/environments';
import { AccountUser, NaluriVideoLoginOptions } from '@/types';
import { getGatewayEndpoint } from '@/utils/getGatewayEndpoint';
import { invariant } from '@/utils/invariant';
import { AnalyticsApi, EVENTS } from './api/analytics.api';
import { NaluriVideoErrorConstants } from './constants/errors';
import { NaluriVideoError } from './errors';
import {
  startRefreshTokenOnInterval,
  stopRefreshTokenOnInterval,
} from './utils/startRefreshTokenOnInterval';
import { TokenStore } from './utils/tokenStore';

export interface NaluriVideoOptions {
  apiKey: string;
  timeoutMs?: number;
  environment?: SupportedEnvironments;
  onRefreshTokenError?: () => Promise<void>;
}

// private
let analyticsApi: AnalyticsApi;

const tokenStore = new TokenStore();

export class NaluriVideo {
  /**
   * Api key of naluri video
   */
  public readonly apiKey: string;

  /**
   * Environment, default to SIT
   */
  public readonly environment: SupportedEnvironments;

  /**
   * Function to be called when refresh token fails
   */
  public readonly onRefreshTokenError?: () => Promise<void>;

  /**
   * @private
   * Initialized Video instance
   */
  private static videoInstance?: NaluriVideo;

  private accountInstance: AccountApi;

  private videoApiInstance?: VideoApi;

  private constructor({
    apiKey,
    environment,
    onRefreshTokenError,
  }: NaluriVideoOptions) {
    this.apiKey = apiKey;

    this.environment = environment ?? SupportedEnvironments.SIT;

    if (onRefreshTokenError) {
      this.onRefreshTokenError = onRefreshTokenError;
    }

    this.accountInstance = new AccountApi({
      apiKey,
      gatewayUrl: getGatewayEndpoint(this.environment),
      tokenStore,
    });
  }

  /**
   * [IMPORTANT]: Initialise this first before using any other methods
   * Initialize a new Naluri Video instance
   * @returns {NaluriVideo}
   */
  public static initialize({
    apiKey,
    timeoutMs,
    environment,
    onRefreshTokenError,
  }: NaluriVideoOptions): void {
    invariant(
      typeof apiKey === 'string' && apiKey,
      'Cannot initialize Naluri Video without api key',
    );

    if (this.videoInstance) {
      return;
    }

    const VideoInstance = new NaluriVideo({
      apiKey,
      environment,
      timeoutMs,
      onRefreshTokenError,
    });

    this.videoInstance = VideoInstance;
  }

  /**
   * Gets a new Naluri Video instance
   * @returns {NaluriVideo}
   */
  public static getInstance(): NaluriVideo {
    invariant(
      !!this.videoInstance,
      'Naluri video instance is not initialized. Please use the initialize method first.',
    );

    return this.videoInstance;
  }

  /**
   * @throws {NaluriVideoError}
   * @returns {string} access token for video room
   */
  public async connectRoom(roomId: string): Promise<string> {
    invariant(
      !!this.videoApiInstance,
      'Naluri video instance is not initialized. Please use the initialize and login method first.',
    );

    if (analyticsApi) analyticsApi.track(EVENTS.CONNECT_ROOM, { roomId });

    try {
      const token = await this.videoApiInstance.authorizeUserForRoom(roomId);
      return token;
    } catch (e) {
      if (analyticsApi)
        analyticsApi.track(EVENTS.CONNECT_ROOM_ERROR, {
          error: e,
          message: (e as Error).message,
        });
      throw e;
    }
  }

  /**
   * Login user to video
   * @param {NaluriVideoLoginOptions} options
   * @returns {PromiseLike<NaluriVideoLoginResponse>}
   */
  async login({
    authorizationCode,
  }: NaluriVideoLoginOptions): Promise<AccountUser> {
    const { user } = await this.accountInstance.login(authorizationCode);

    if (this.environment !== SupportedEnvironments.DEV) {
      analyticsApi = new AnalyticsApi(
        user.id,
        getGatewayEndpoint(this.environment),
      );

      analyticsApi.identify({
        tenant_id: user.tenantId,
        country: user.country,
        language: user.preferredLanguage,
      });
    }

    this.videoApiInstance = new VideoApi({
      apiKey: this.apiKey,
      environment: this.environment,
      tokenStore,
      accountApi: this.accountInstance,
    });

    await startRefreshTokenOnInterval(
      this.accountInstance,
      tokenStore,
      analyticsApi,
      this.onRefreshTokenError,
    );

    return user;
  }

  public get isInitialized(): boolean {
    return !!this.videoApiInstance;
  }

  public async logout() {
    if (analyticsApi) analyticsApi.track(EVENTS.LOGOUT);
    const refreshToken = tokenStore.getItem('refreshToken');

    try {
      if (refreshToken) await this.accountInstance.logout(refreshToken);
    } catch (err) {
      if (analyticsApi) analyticsApi.track(EVENTS.LOGOUT_ERROR, { error: err });

      throw new NaluriVideoError({
        message: (err as Error).message || 'Logout failed',
        code: NaluriVideoErrorConstants.LOGOUT_FAILED,
        statusCode: 500,
      });
    } finally {
      if (analyticsApi) analyticsApi.cleanup();
      tokenStore.clear();
      stopRefreshTokenOnInterval();
    }

    return { success: true };
  }

  destroy() {
    NaluriVideo.videoInstance = undefined;
  }
}
